// Shared by both forks.
import { useCallback } from 'react';
import {
	executeSearch,
	getTextSearchField,
	isReactAssetOverviewActive,
} from '../helpers/searchUtils';
import { emitSimilaritySearchEvent } from '../NLSSearch/utils';
import type { SelectedSearchOption, TextSearchParams } from '../helpers/types';
import { useDefaultSearchAction } from './useDefaultSearchAction';
import { useSpotlightCapabilities } from './useSpotlightCapabilities';

type Options = {
	searchTerm: string;
	selectedSearchOptions: SelectedSearchOption[];
	isApplyingSimilarityFilter: boolean;
	isHybridSearchOn: boolean;
	addSearchTermToRecentSearches: (term: string) => void;
	cleanUp: () => void;
};

export function useRunSearch({
	searchTerm,
	selectedSearchOptions,
	isApplyingSimilarityFilter,
	isHybridSearchOn,
	addSearchTermToRecentSearches,
	cleanUp,
}: Options) {
	const { canShowStickySearch, canShowRecentSearches } = useSpotlightCapabilities();

	const executeNLSSearch = useCallback(
		(term: string) => {
			emitSimilaritySearchEvent(isHybridSearchOn, {
				similarToAssetIds: null,
				similarToImageData: null,
				similarToURL: null,
				similarToText: term,
			});

			if (canShowRecentSearches) {
				addSearchTermToRecentSearches(term);
			}
		},
		[canShowRecentSearches, addSearchTermToRecentSearches, isHybridSearchOn],
	);

	const executeSearchWithOptions = useCallback(
		(term: string) => {
			const params: TextSearchParams = {
				field: getTextSearchField(canShowStickySearch),
				value: term,
				filterType: 'add',
				filterKey: '',
				searchOptions: selectedSearchOptions,
			};

			executeSearch(isHybridSearchOn, params);
		},
		[selectedSearchOptions, canShowStickySearch, isHybridSearchOn],
	);

	const executeRegularSearch = useCallback(
		(term: string) => {
			const params: TextSearchParams = {
				field: getTextSearchField(canShowStickySearch),
				value: term,
				filterType: 'add',
				filterKey: '',
				...(isApplyingSimilarityFilter ? { similarToText: term } : {}),
			};

			executeSearch(isHybridSearchOn, params, isApplyingSimilarityFilter);
		},
		[canShowStickySearch, isApplyingSimilarityFilter, isHybridSearchOn],
	);

	const runSearch = useCallback(
		(termToSearch: string) => {
			// NLS search
			if (isApplyingSimilarityFilter && isReactAssetOverviewActive()) {
				executeNLSSearch(termToSearch);

				return;
			}

			// Search options (NOTE: searches with options are NOT added to recent searches, this is on purpose, expected behavior)
			if (selectedSearchOptions.length > 0) {
				executeSearchWithOptions(termToSearch);

				return;
			}

			// Normal search
			executeRegularSearch(termToSearch);

			// Add to recent searches
			if (canShowRecentSearches) {
				addSearchTermToRecentSearches(termToSearch);
			}
		},
		[
			isApplyingSimilarityFilter,
			canShowRecentSearches,
			selectedSearchOptions,
			addSearchTermToRecentSearches,
			executeNLSSearch,
			executeSearchWithOptions,
			executeRegularSearch,
		],
	);

	const defaultSearchAction = useDefaultSearchAction({
		searchTerm,
		runSearch,
		cleanUp,
	});

	return { defaultSearchAction };
}
